import React, { useState, useEffect, useRef } from 'react';
import {
  FaCog,
  FaDatabase,
  FaCloud,
  FaShieldAlt,
  FaSave,
  FaServer,
  FaSync,
  FaEnvelope,
  FaPalette,
  FaIdBadge,
  FaBuilding,
  FaExclamationTriangle,
  FaRedo,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaPlay,
  FaStop,
  FaInfoCircle,
  FaBolt
} from 'react-icons/fa';
import CONFIG from '../../../src/config';

const SuperAdminSettingsContent = () => {
  const [settings, setSettings] = useState({
    darkMode: true,
    emailNotifications: true,
    autoBackup: true,
    twoFactorAuth: false,
    s3Enabled: false,
    companyExtractLimit: 3,
    apiRateLimit: 100,
    dataRetentionDays: 365,
    systemName: 'Financial Data Platform',
    timezone: 'UTC+00:00',
    adminId: 'SA-2024-001'
  });

  const [tempCompanyLimit, setTempCompanyLimit] = useState(3); // Temporary value for input
  const [isSaving, setIsSaving] = useState(false);
  const [showLimitConfirm, setShowLimitConfirm] = useState(false);
  const [showS3Confirm, setShowS3Confirm] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);
  const [pendingSettingKey, setPendingSettingKey] = useState('');
  const [s3Status, setS3Status] = useState({
    isRunning: false,
    companiesToday: 0,
    maxCompanies: 3,
    lastStarted: null,
    lastStopped: null,
    dailyStats: {}
  });
  const [loading, setLoading] = useState(true);
  const [s3ActionLoading, setS3ActionLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  
  const [manuallyStopped, setManuallyStopped] = useState(false);
  const [processingTriggered, setProcessingTriggered] = useState(false);
  const prevRunningState = useRef(false);
  const statusIntervalRef = useRef(null);

  // Enhanced showNotification function
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 5000);
  };

  // FIXED: Use correct method name from config.js
  const checkAuthAndRedirect = async () => {
    const token = CONFIG.getToken(); // FIXED: Changed from getValidToken() to getToken()
    if (!token) {
      showNotification('error', 'Session expired. Please login again.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      return false;
    }
    return true;
  };

  // Helper function to get S3 control URLs without duplication
  const getS3ControlUrl = (endpoint, params = '') => {
    // For S3_CONTROL endpoints, we need to handle them specially
    // because they already have /api/v1 prefix in the endpoint definition
    // Use getRootUrl instead of getApiUrl to avoid duplication
    return `${CONFIG.API.BASE_URL}${endpoint}${params}`;
  };

  // Fetch S3 Status - FIXED VERSION
  const fetchS3Status = async () => {
    try {
      if (!await checkAuthAndRedirect()) {
        setLoading(false);
        return;
      }

      const response = await fetch(getS3ControlUrl(CONFIG.API.S3_CONTROL.STATUS), {
        headers: CONFIG.getAuthHeaders(),
        cache: 'no-cache'
      });

      if (response.ok) {
        const data = await response.json();
        const status = data.status || {};
        
        const isRunning = status.is_auto_running || false;
        const maxCompanies = status.daily_stats?.max_companies_per_day || 3;
        
        // Update S3 status
        const newS3Status = {
          isRunning: isRunning,
          companiesToday: status.daily_stats?.companies_processed || 0,
          maxCompanies: maxCompanies,
          lastStarted: status.last_started,
          lastStopped: status.last_stopped,
          dailyStats: status.daily_stats || {}
        };
        
        setS3Status(newS3Status);

        // Update settings.s3Enabled to match actual status
        setSettings(prev => ({
          ...prev,
          s3Enabled: isRunning,
          companyExtractLimit: maxCompanies
        }));

        // Sync temporary input value with actual limit
        setTempCompanyLimit(maxCompanies);

        // Track running state changes
        if (prevRunningState.current !== isRunning) {
          console.log(`S3 Status changed: ${prevRunningState.current} -> ${isRunning}`);
          if (isRunning) {
            showNotification('info', 'S3 processing is now active');
          } else {
            showNotification('warning', 'S3 processing has stopped');
          }
          prevRunningState.current = isRunning;
        }
        
        // Reset manual stop flag if we see it's running
        if (isRunning && manuallyStopped) {
          setManuallyStopped(false);
        }
        
      } else if (response.status === 401) {
        // Clear invalid token
        localStorage.removeItem('access_token');
        sessionStorage.removeItem('access_token');
        localStorage.removeItem('currentUser');
        showNotification('error', 'Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        const error = await response.json();
        console.error('S3 status fetch error:', error);
        showNotification('error', `Failed to fetch S3 status: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching S3 status:', error);
      // Don't show notification for polling errors
    } finally {
      setLoading(false);
    }
  };

  // Start S3 Processing - FIXED
  const startS3Processing = async () => {
    if (!await checkAuthAndRedirect()) return;
    
    setS3ActionLoading(true);
    setManuallyStopped(false);
    try {
      const response = await fetch(getS3ControlUrl(CONFIG.API.S3_CONTROL.START), {
        method: 'POST',
        headers: CONFIG.getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        showNotification('success', data.details?.message || 'S3 processing started successfully');
        
        // Update local state immediately
        setSettings(prev => ({ ...prev, s3Enabled: true }));
        
        // Wait a moment then refresh status
        setTimeout(async () => {
          await fetchS3Status();
        }, 1000);
        
      } else if (response.status === 401) {
        showNotification('error', 'Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        const error = await response.json();
        showNotification('error', error.detail || 'Failed to start S3 processing');
      }
    } catch (error) {
      console.error('Error starting S3 processing:', error);
      showNotification('error', 'Network error. Please try again.');
    } finally {
      setS3ActionLoading(false);
    }
  };

  // Stop S3 Processing - FIXED
  const stopS3Processing = async () => {
    if (!await checkAuthAndRedirect()) return;
    
    setS3ActionLoading(true);
    setManuallyStopped(true);
    try {
      const response = await fetch(getS3ControlUrl(CONFIG.API.S3_CONTROL.STOP), {
        method: 'POST',
        headers: CONFIG.getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        showNotification('success', data.details?.message || 'S3 processing stopped successfully');
        
        // Update local state immediately
        setSettings(prev => ({ ...prev, s3Enabled: false }));
        
        // Wait a moment then refresh status
        setTimeout(async () => {
          await fetchS3Status();
        }, 1000);
        
      } else if (response.status === 401) {
        showNotification('error', 'Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        const error = await response.json();
        showNotification('error', error.detail || 'Failed to stop S3 processing');
      }
    } catch (error) {
      console.error('Error stopping S3 processing:', error);
      showNotification('error', 'Network error. Please try again.');
    } finally {
      setS3ActionLoading(false);
    }
  };

  const setDailyLimit = async (limit) => {
    if (!await checkAuthAndRedirect()) return;
    
    try {
      const response = await fetch(getS3ControlUrl(CONFIG.API.S3_CONTROL.SET_DAILY_LIMIT) + `?limit=${limit}`, {
        method: 'POST',
        headers: CONFIG.getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        showNotification('success', data.message || `Daily limit updated to ${limit} companies`);
        
        // Update local state
        setSettings(prev => ({ ...prev, companyExtractLimit: limit }));
        setTempCompanyLimit(limit);
        
        // Refresh full status
        await fetchS3Status();
      } else if (response.status === 401) {
        showNotification('error', 'Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        const error = await response.json();
        showNotification('error', error.detail || 'Failed to update daily limit');
      }
    } catch (error) {
      console.error('Error setting daily limit:', error);
      showNotification('error', 'Network error. Please try again.');
    }
  };

  const resetDailyCounts = async () => {
    if (!await checkAuthAndRedirect()) return;
    
    setResetLoading(true);
    try {
      const response = await fetch(getS3ControlUrl(CONFIG.API.S3_CONTROL.RESET_DAILY_COUNTS), {
        method: 'POST',
        headers: CONFIG.getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        showNotification('success', data.message || 'Daily counts reset successfully');
        
        await fetchS3Status();
      } else if (response.status === 401) {
        showNotification('error', 'Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        const error = await response.json();
        showNotification('error', error.detail || 'Failed to reset daily counts');
      }
    } catch (error) {
      console.error('Error resetting daily counts:', error);
      showNotification('error', 'Network error. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Helper Functions
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Event Handlers
  const handleS3Toggle = async (enabled) => {
    if (enabled && s3Status.isRunning) {
      showNotification('info', 'S3 processing is already running');
      return;
    }
    
    if (!enabled && !s3Status.isRunning) {
      showNotification('info', 'S3 processing is already stopped');
      return;
    }
    
    // Show confirmation dialog
    setPendingSettingKey('s3Enabled');
    setPendingValue(enabled);
    setShowS3Confirm(true);
  };

  const handleSetDailyLimit = async () => {
    const limit = parseInt(tempCompanyLimit);
    
    if (isNaN(limit) || limit < 1 || limit > 10) {
      showNotification('error', 'Daily limit must be a number between 1 and 10');
      return;
    }
    
    if (limit > 3) {
      setPendingSettingKey('companyExtractLimit');
      setPendingValue(limit);
      setShowLimitConfirm(true);
    } else {
      await setDailyLimit(limit);
    }
  };

  const handleResetDailyCounts = async () => {
    await resetDailyCounts();
  };

  const handleSettingChange = (key, value) => {
    // For S3 toggle, handle directly with confirmation
    if (key === 's3Enabled') {
      handleS3Toggle(value);
      return;
    }
    
    // For other settings, update directly
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle company limit input change (only updates temporary value)
  const handleCompanyLimitInputChange = (value) => {
    const limit = parseInt(value);
    if (!isNaN(limit) && limit >= 1 && limit <= 10) {
      setTempCompanyLimit(limit);
    } else if (value === '') {
      setTempCompanyLimit('');
    }
  };

  const confirmLimitChange = async () => {
    if (pendingSettingKey && pendingValue !== null) {
      const limit = parseInt(pendingValue);
      setSettings(prev => ({
        ...prev,
        [pendingSettingKey]: limit
      }));
      setTempCompanyLimit(limit);
      await setDailyLimit(limit);
    }
    setShowLimitConfirm(false);
    setPendingValue(null);
    setPendingSettingKey('');
  };

  const confirmS3Toggle = async () => {
    if (pendingSettingKey && pendingValue !== null) {
      if (pendingValue) {
        await startS3Processing();
      } else {
        await stopS3Processing();
      }
    }
    setShowS3Confirm(false);
    setPendingValue(null);
    setPendingSettingKey('');
  };

  const cancelChange = () => {
    setShowLimitConfirm(false);
    setShowS3Confirm(false);
    setPendingValue(null);
    setPendingSettingKey('');
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      showNotification('success', 'Settings saved successfully!');
    }, 1000);
  };

  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (s3Status.maxCompanies === 0) return 0;
    const percentage = (s3Status.companiesToday / s3Status.maxCompanies) * 100;
    return Math.min(100, Math.round(percentage));
  };

  // Fetch initial data - FIXED
  useEffect(() => {
    fetchS3Status();
    
    // Poll every 10 seconds for status updates
    statusIntervalRef.current = setInterval(fetchS3Status, 10000);
    
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, []);

  // Update status when manually stopped flag changes
  useEffect(() => {
    if (manuallyStopped && s3Status.isRunning) {
      // User manually stopped but backend is still running
      console.log('Manual stop detected, updating toggle state');
      setSettings(prev => ({ ...prev, s3Enabled: false }));
    }
  }, [manuallyStopped, s3Status.isRunning]);

  const systemInfo = [
    { label: 'System Version', value: 'v2.1.0', icon: <FaCog />, color: 'text-blue-500' },
    { label: 'Database', value: 'PostgreSQL 14.5', icon: <FaDatabase />, color: 'text-green-500' },
    { label: 'Last Updated', value: new Date().toLocaleDateString(), icon: <FaSync />, color: 'text-purple-500' },
    { label: 'API Status', value: 'Operational', icon: <FaCloud />, color: 'text-green-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 border border-green-200 dark:border-green-700'
            : notification.type === 'error'
            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 border border-red-200 dark:border-red-700'
            : notification.type === 'warning'
            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700'
            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
        }`}>
          {notification.type === 'success' ? (
            <FaCheck className="text-green-600 dark:text-green-400" />
          ) : notification.type === 'error' ? (
            <FaTimes className="text-red-600 dark:text-red-400" />
          ) : notification.type === 'warning' ? (
            <FaExclamationTriangle className="text-yellow-600 dark:text-yellow-400" />
          ) : (
            <FaInfoCircle className="text-blue-600 dark:text-blue-400" />
          )}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Confirmation Dialog for Company Extraction Limit */}
      {showLimitConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-yellow-100 dark:bg-yellow-900 p-2 rounded-full">
                <FaExclamationTriangle className="text-yellow-600 dark:text-yellow-300 text-xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Company Limit Exceeded
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                You are trying to set the company extraction limit to <span className="font-bold">{pendingValue}</span>.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                The system only allows a maximum of <span className="font-bold text-red-500">3 companies</span> for data extraction.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                Setting a higher limit may affect system performance and data processing speed.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelChange}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmLimitChange}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                Confirm Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for S3 Toggle */}
      {showS3Confirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-full ${pendingValue ? 'bg-green-100 dark:bg-green-900' : 'bg-red-100 dark:bg-red-900'}`}>
                {pendingValue ? (
                  <FaPlay className="text-green-600 dark:text-green-300 text-xl" />
                ) : (
                  <FaStop className="text-red-600 dark:text-red-300 text-xl" />
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {pendingValue ? 'Enable' : 'Disable'} S3 Bucket Processing
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                {pendingValue 
                  ? 'You are about to start automatic S3 processing. This will:'
                  : 'You are about to stop automatic S3 processing. This will:'
                }
              </p>
              <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1">
                {pendingValue ? (
                  <>
                    <li>Start automatic processing of PDFs from S3 bucket</li>
                    <li>Process up to {settings.companyExtractLimit} companies per day</li>
                    <li>Extract financial statements automatically</li>
                    <li>Generate Excel files with consolidated data</li>
                  </>
                ) : (
                  <>
                    <li>Stop all automatic S3 processing</li>
                    <li>Pause company extraction from S3 bucket</li>
                    <li>Current operations will complete gracefully</li>
                    <li>Can be restarted at any time</li>
                  </>
                )}
              </ul>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelChange}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                disabled={s3ActionLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmS3Toggle}
                disabled={s3ActionLoading}
                className={`px-4 py-2 rounded-lg text-white transition ${s3ActionLoading ? 'opacity-50 cursor-not-allowed' : ''} ${
                  pendingValue 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {s3ActionLoading ? (
                  <span className="flex items-center gap-2">
                    <FaSpinner className="animate-spin" />
                    Processing...
                  </span>
                ) : (
                  pendingValue ? 'Start Processing' : 'Stop Processing'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">System Settings</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Configure system-wide settings and integrations
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2 space-y-6">
          {/* S3 PROCESSING STATUS */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <FaCloud className="text-indigo-600 text-xl" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    S3 Bucket Processing
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Automatically process PDFs from S3 bucket
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${
                s3Status.isRunning 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                  : manuallyStopped
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                <div className={`h-2 w-2 rounded-full ${
                  s3Status.isRunning ? 'bg-green-500 animate-pulse' : 
                  manuallyStopped ? 'bg-yellow-500' : 'bg-gray-500'
                }`}></div>
                {s3Status.isRunning ? 'Running' : manuallyStopped ? 'Manually Stopped' : 'Stopped'}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Daily Progress</div>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {s3Status.companiesToday}/{s3Status.maxCompanies}
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-700 dark:text-gray-300">companies</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mt-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${getProgressPercentage()}%` 
                    }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                  {getProgressPercentage()}% complete
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Last Activity</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Started:</span>
                    <span className="font-medium text-sm">{formatDateTime(s3Status.lastStarted)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Stopped:</span>
                    <span className="font-medium text-sm">{formatDateTime(s3Status.lastStopped)}</span>
                  </div>
                </div>
              </div>
            </div>

            
            {/* S3 TOGGLE SWITCH AND ACTIONS */}
            <div className="space-y-4">
              {/* Toggle Switch for S3 Processing */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  {s3Status.isRunning ? (
                    <FaPlay className="text-green-500" />
                  ) : (
                    <FaStop className="text-red-500" />
                  )}
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Automatic S3 Processing
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {s3Status.isRunning 
                        ? 'S3 processing is currently active' 
                        : manuallyStopped
                        ? 'S3 processing was manually stopped'
                        : 'S3 processing is currently paused'}
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.s3Enabled}
                    onChange={(e) => handleSettingChange('s3Enabled', e.target.checked)}
                    disabled={s3ActionLoading}
                  />
                  <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:h-5 after:w-5 after:rounded-full after:transition-all peer-checked:after:translate-x-full ${s3ActionLoading ? 'opacity-50' : ''}`} />
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleResetDailyCounts}
                  disabled={resetLoading || s3ActionLoading || !s3Status.isRunning}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg flex items-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!s3Status.isRunning ? "Start S3 processing first" : "Reset today's processing count"}
                >
                  <FaRedo />
                  {resetLoading ? 'Resetting...' : 'Reset Daily Count'}
                </button>

                <button
                  onClick={fetchS3Status}
                  disabled={s3ActionLoading || resetLoading}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 transition disabled:opacity-50"
                >
                  <FaSync />
                  Refresh Status
                </button>
                
                <button
                  onClick={() => handleS3Toggle(!s3Status.isRunning)}
                  disabled={s3ActionLoading}
                  className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 transition ${s3ActionLoading ? 'opacity-50' : ''} ${
                    s3Status.isRunning 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {s3Status.isRunning ? (
                    <>
                      <FaStop />
                      Stop Processing
                    </>
                  ) : (
                    <>
                      <FaPlay />
                      Start Processing
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* S3 LIMIT CONFIGURATION - MOVED ABOVE GENERAL SETTINGS */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-6">
              <FaBuilding className="text-indigo-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  S3 Processing Limit Configuration
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Configure daily processing limits for S3 bucket
                </p>
              </div>
            </div>

            {/* Company Limit Configuration */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Daily Company Processing Limit
              </label>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <FaBuilding className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="number"
                    min="1"
                    max="10"
                    className="w-1/2 pl-10 px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    value={tempCompanyLimit}
                    onChange={(e) => handleCompanyLimitInputChange(e.target.value)}
                    disabled={s3ActionLoading}
                  />
                </div>
                <button
                  onClick={handleSetDailyLimit}
                  disabled={s3ActionLoading || tempCompanyLimit === s3Status.maxCompanies || tempCompanyLimit === ''}
                  className={`px-4 py-2 rounded-lg ${s3ActionLoading || tempCompanyLimit === s3Status.maxCompanies || tempCompanyLimit === '' ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'} text-white transition`}
                  title={tempCompanyLimit === s3Status.maxCompanies ? "Limit is already set to this value" : "Click to update the limit"}
                >
                  Update Limit
                </button>
              </div>
              <div className="mt-3 space-y-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Maximum companies to process per day from S3 bucket (Range: 1-10)
                </p>
                {tempCompanyLimit > 3 && tempCompanyLimit !== s3Status.maxCompanies && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <FaExclamationTriangle /> Warning: Exceeding 3 may impact performance. Click "Update Limit" to apply.
                  </p>
                )}
                {tempCompanyLimit === s3Status.maxCompanies && (
                  <p className="text-xs text-green-500 flex items-center gap-1">
                    <FaCheck /> Current limit: {s3Status.maxCompanies} companies
                  </p>
                )}
                {s3Status.isRunning && (
                  <p className="text-xs text-blue-500 flex items-center gap-1">
                    <FaInfoCircle /> Processing active: {s3Status.companiesToday}/{s3Status.maxCompanies} companies today
                  </p>
                )}
                {tempCompanyLimit !== s3Status.maxCompanies && tempCompanyLimit !== '' && (
                  <p className="text-xs text-yellow-500 flex items-center gap-1">
                    <FaInfoCircle /> Click "Update Limit" button to apply the new limit
                  </p>
                )}
              </div>
            </div>

           
          </div>

          {/* GENERAL SETTINGS - MOVED BELOW S3 LIMIT */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-6">
              <FaCog className="text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                General Settings
              </h3>
            </div>

            {/* Toggles */}
            <div className="space-y-4">
              {[
                {
                  label: 'Dark Mode',
                  desc: 'Enable dark theme',
                  icon: <FaPalette className="text-blue-500" />,
                  key: 'darkMode'
                },
                {
                  label: 'Email Notifications',
                  desc: 'Receive system alerts',
                  icon: <FaEnvelope className="text-green-500" />,
                  key: 'emailNotifications'
                },
                {
                  label: 'Auto Backup',
                  desc: 'Daily database backup',
                  icon: <FaDatabase className="text-orange-500" />,
                  key: 'autoBackup'
                },
                {
                  label: 'Two-Factor Authentication',
                  desc: 'Mandatory 2FA for admins',
                  icon: <FaShieldAlt className="text-red-500" />,
                  key: 'twoFactorAuth'
                }
              ].map(item => (
                <div
                  key={item.key}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {item.icon}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">{item.label}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={settings[item.key]}
                      onChange={(e) => handleSettingChange(item.key, e.target.checked)}
                      disabled={s3ActionLoading}
                    />
                    <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:h-5 after:w-5 after:rounded-full after:transition-all peer-checked:after:translate-x-full ${s3ActionLoading ? 'opacity-50' : ''}`} />
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-6">
              <FaServer className="text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                System Information
              </h3>
            </div>

            <div className="space-y-4">
              {systemInfo.map((info, index) => (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-3 mb-1">
                    <span className={info.color}>{info.icon}</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {info.label}
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white ml-7">
                    {info.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* S3 DETAILED INFO */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              S3 Processing Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Status</span>
                <span className={`font-medium ${s3Status.isRunning ? 'text-green-600' : manuallyStopped ? 'text-yellow-600' : 'text-red-600'}`}>
                  {s3Status.isRunning ? 'Active' : manuallyStopped ? 'Manually Stopped' : 'Inactive'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Daily Limit</span>
                <span className="font-medium">{s3Status.maxCompanies} companies</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Processed Today</span>
                <span className="font-medium">{s3Status.companiesToday} companies</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Remaining Today</span>
                <span className="font-medium">
                  {Math.max(0, s3Status.maxCompanies - s3Status.companiesToday)} companies
                </span>
              </div>
              {s3Status.dailyStats && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Files Processed</span>
                    <span className="font-medium">{s3Status.dailyStats.files_processed || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Files Skipped</span>
                    <span className="font-medium">{s3Status.dailyStats.files_skipped || 0}</span>
                  </div>
                </>
              )}
              <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  <strong>Note:</strong> When S3 processing is enabled, the system will automatically:
                </p>
                <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                  <li>• Scan S3 bucket for new PDFs</li>
                  <li>• Process up to {s3Status.maxCompanies} companies per day</li>
                  <li>• Extract financial statements automatically</li>
                  <li>• Generate consolidated Excel files</li>
                </ul>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  <FaInfoCircle className="inline mr-1" />
                  Use "Trigger Immediate Processing" for on-demand extraction.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={isSaving || s3ActionLoading}
            className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:from-purple-700 hover:to-pink-700 transition"
          >
            <FaSave />
            {isSaving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminSettingsContent;